import { Component, EventEmitter, Input, OnDestroy, Output, SecurityContext } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import * as moment from 'moment';
import { tap } from 'rxjs/operators';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

interface IHtmlAudioElementState {
    isPlaying: boolean;
    readableCurrentTime: string;
    readableDuration: string;
    duration: number;
    currentTime: number;
    canplay: boolean;
    error: boolean;
    hasEnded: boolean;
}

class AudioState implements IHtmlAudioElementState {
    private readonly _stateSubject: Subject<IHtmlAudioElementState>;
    private readonly _audioObj: HTMLAudioElement;
    private readonly _eventsList: string[] = [
        'ended',
        'error',
        'play',
        'playing',
        'pause',
        'timeupdate',
        'canplay',
        'loadedmetadata',
        'loadstart'
    ];
    isPlaying: boolean;
    canplay: boolean;
    currentTime: number;
    duration: number;
    error: boolean;
    readableCurrentTime: string;
    readableDuration: string;
    hasEnded: boolean;

    constructor(audioObj: HTMLAudioElement) {
        this._audioObj = audioObj;
        this._stateSubject = new Subject<IHtmlAudioElementState>();
        for (const event of this._eventsList) {
            this._audioObj.addEventListener(event, this._updater);
        }
    }

    private static formatTime(time: number, format: string = 'HH:mm:ss') {
        const momentTime = time * 1000;
        return moment.utc(momentTime).format(format);
    }

    private _updater: (event: Event) => void = (event: Event) => {
        console.debug(`_audioObjEventHandler`, event);
        switch (event.type) {
            case 'canplay':
                this.duration = this._audioObj.duration;
                this.readableDuration = AudioState.formatTime(this.duration);
                if (this.currentTime == null) {
                    this.currentTime = 0;
                    this.readableCurrentTime = AudioState.formatTime(this.currentTime);
                }
                this.error = false;
                this.canplay = true;
                break;
            case 'playing':
                this.isPlaying = true;
                break;
            case 'pause':
                this.isPlaying = false;
                break;
            case 'timeupdate':
                this.currentTime = this._audioObj.currentTime;
                this.readableCurrentTime = AudioState.formatTime(this.currentTime);
                break;
            case 'error':
                this.resetState();
                this.error = true;
                break;
            case 'ended':
                this.hasEnded = true;
                break;
        }
        this._stateSubject.next(this.currentState);
    };

    private resetState() {
        const defaultState: IHtmlAudioElementState = {
            isPlaying: false,
            readableCurrentTime: null,
            readableDuration: null,
            duration: null,
            currentTime: null,
            canplay: false,
            error: false,
            hasEnded: false
        };
        this.isPlaying = defaultState.isPlaying;
        this.readableCurrentTime = defaultState.readableCurrentTime;
        this.readableDuration = defaultState.readableDuration;
        this.duration = defaultState.duration;
        this.currentTime = defaultState.currentTime;
        this.canplay = defaultState.canplay;
        this.error = defaultState.error;
        this.hasEnded = defaultState.hasEnded;
    }

    get currentState(): IHtmlAudioElementState {
        return {
            isPlaying: this.isPlaying,
            readableCurrentTime: this.readableCurrentTime,
            readableDuration: this.readableDuration,
            duration: this.duration,
            currentTime: this.currentTime,
            canplay: this.canplay,
            error: this.error,
            hasEnded: this.hasEnded
        };
    }

    asObservable(): Observable<IHtmlAudioElementState> {
        return this._stateSubject.asObservable();
    }
}

@Component({
    selector: 'app-audio-player',
    templateUrl: './audio-player.component.html',
    styleUrls: ['./audio-player.component.css']
})
export class AudioPlayerComponent implements OnDestroy {
    private readonly timeStep: number = 15;
    private readonly audioObj: HTMLAudioElement;
    private readonly audioObjState: AudioState;
    @Output() ended: EventEmitter<void>;
    @Input() fastBackwardEnabled: boolean = true;
    @Input() stepBackwardEnabled: boolean = true;
    @Input() stepForwardEnabled: boolean = true;
    @Input() fastForwardEnabled: boolean = true;
    @Input() disabled: boolean = false;

    get isPlaying(): boolean {
        return this.audioObjState != null && this.audioObjState.isPlaying === true;
    }

    constructor(private sanitizer: DomSanitizer) {
        this.ended = new EventEmitter<void>();
        this.audioObj = new Audio();
        this.audioObjState = new AudioState(this.audioObj);
        this.audioObjState
            .asObservable()
            .pipe(
                tap(state => {
                    if (state.hasEnded) {
                        this.ended.emit();
                    }
                })
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        if (this.isPlaying) {
            this.audioObj.pause();
        }
    }

    @Input() set src(value: SafeResourceUrl) {
        if (value == null) {
            console.warn(`Preventing from setting audio src to null`);
            return;
        }
        this.audioObj.src = this.sanitizer.sanitize(SecurityContext.RESOURCE_URL, value);
    }

    onPlay(): Promise<void> {
        if (!this.mainButtonDisabled) {
            return this.audioObj.play();
        }
    }

    onPause(): void {
        if (!this.mainButtonDisabled) {
            this.audioObj.pause();
        }
    }

    onFastBackward(): void {
        if (!this.fastBackwardDisabled) {
            this.audioObj.currentTime = 0;
        }
    }

    onStepBackward() {
        if (!this.stepBackwardDisabled) {
            this.audioObj.currentTime = Math.max(0, this.audioObj.currentTime - this.timeStep);
        }
    }

    get error(): boolean {
        return this.audioObjState.error;
    }

    onStepForward() {
        if (!this.stepForwardDisabled) {
            this.audioObj.currentTime = Math.min(
                this.audioObj.duration,
                this.audioObj.currentTime + this.timeStep
            );
        }
    }

    onFastForward() {
        if (!this.fastForwardDisabled) {
            this.audioObj.currentTime = this.audioObj.duration;
        }
    }

    get fastBackwardDisabled(): boolean {
        return (
            this.disabled ||
            !this.fastBackwardEnabled ||
            this.audioObj.currentTime == null ||
            this.audioObj.currentTime === 0
        );
    }

    get stepBackwardDisabled(): boolean {
        return (
            this.disabled ||
            !this.stepBackwardEnabled ||
            this.audioObj.currentTime == null ||
            this.audioObj.currentTime === 0
        );
    }

    get mainButtonDisabled(): boolean {
        return this.disabled;
    }

    get stepForwardDisabled(): boolean {
        return (
            this.disabled ||
            !this.stepForwardEnabled ||
            this.audioObj.currentTime == null ||
            this.audioObj.currentTime === this.audioObj.duration
        );
    }

    get fastForwardDisabled(): boolean {
        return (
            this.disabled ||
            !this.fastForwardEnabled ||
            this.audioObj.currentTime == null ||
            this.audioObj.currentTime === this.audioObj.duration
        );
    }
}



// WEBPACK FOOTER //
// ./src/app/components/audio-player/audio-player.component.ts